import { useEffect, useRef, useState } from "react";
import { createChart, ColorType, CrosshairMode } from "lightweight-charts";
import api from "../lib/api";
import { clsx } from "../lib/format";

const TFS = [[15, "15s"], [30, "30s"], [60, "1m"], [300, "5m"], [900, "15m"], [3600, "1h"], [14400, "4h"], [86400, "1d"]];
const MAX_BARS = 120;

function pickTf(activity) {
  const oldest = activity.reduce((m, a) => Math.min(m, new Date(a.created_at).getTime() / 1000), Date.now() / 1000);
  const span = Date.now() / 1000 - oldest + 300;
  return TFS.find(([sec]) => span / sec <= MAX_BARS) || TFS[TFS.length - 1];
}
const UP = "#10B981", DOWN = "#EF4444", BLUE = "#3B82F6", GOLD = "#F59E0B", MUTED = "#64748B";

const bucket = (a, tf) => Math.floor(new Date(a.created_at).getTime() / 1000 / tf) * tf;
const pair = (x, y, a, b) => (x && y ? `${x}${a}/${y}${b}` : x ? (x > 1 ? `${x}${a}` : a) : (y > 1 ? `${y}${b}` : b));

function groupMarkers(time, evs) {
  const n = (k, re) => evs.filter((e) => e.kind === k && (!re || re.test(e.message))).length;
  const buys = n("open", /\bBUY\b/i), sells = n("open", /\bSELL\b/i), wins = n("win"), losses = n("loss");
  const ctl = evs.filter((e) => !["open", "win", "loss"].includes(e.kind));
  const out = [];
  if (ctl.length) out.push({ time, position: "aboveBar", color: ctl.some((e) => e.kind === "start") ? GOLD : MUTED, shape: "square", text: "" });
  if (wins || losses) out.push({ time, position: "aboveBar", color: wins >= losses ? UP : DOWN, shape: "circle", text: pair(wins, losses, "W", "L") });
  if (buys || sells) out.push({ time, position: "belowBar", color: buys >= sells ? UP : DOWN, shape: buys >= sells ? "arrowUp" : "arrowDown", text: pair(buys, sells, "B", "S") });
  return out;
}

export default function BotActivityChart({ lid, asset, activity, running }) {
  const boxRef = useRef(null);
  const seriesRef = useRef(null);
  const chartRef = useRef(null);
  const rangeRef = useRef([0, 0]);
  const evRef = useRef({});
  const [tip, setTip] = useState(null);
  const [candles, setCandles] = useState([]);
  const [tfSec, tfLabel] = pickTf(activity);

  useEffect(() => {
    const chart = createChart(boxRef.current, {
      localization: { locale: "en-US" },
      layout: { background: { type: ColorType.Solid, color: "transparent" }, textColor: "#94A3B8", fontFamily: "JetBrains Mono, monospace", fontSize: 10 },
      grid: { vertLines: { color: "rgba(255,255,255,0.03)" }, horzLines: { color: "rgba(255,255,255,0.03)" } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.15, bottom: 0.15 } },
      timeScale: { borderVisible: false, timeVisible: true, secondsVisible: true, rightOffset: 3 },
      handleScroll: false, handleScale: false,
      width: boxRef.current.clientWidth, height: 170,
    });
    seriesRef.current = chart.addCandlestickSeries({ upColor: UP, downColor: DOWN, borderUpColor: UP, borderDownColor: DOWN, wickUpColor: UP, wickDownColor: DOWN });
    chartRef.current = chart;
    chart.subscribeCrosshairMove((p) => {
      const evs = p.time ? evRef.current[p.time] : null;
      setTip(evs && p.point ? { x: p.point.x, y: p.point.y, evs } : null);
    });
    const ro = new ResizeObserver(() => boxRef.current && chart.applyOptions({ width: boxRef.current.clientWidth }));
    ro.observe(boxRef.current);
    return () => { ro.disconnect(); chart.remove(); };
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/market/candles", { params: { symbol: asset, timeframe: tfLabel, count: MAX_BARS } })
      .then((r) => alive && setCandles(r.data.candles)).catch(() => {});
    load();
    const t = setInterval(load, running ? 5000 : 30000);
    return () => { alive = false; clearInterval(t); };
  }, [asset, running, tfLabel]);

  useEffect(() => {
    if (!seriesRef.current || !candles.length) return;
    const first = rangeRef.current[0] === 0 || rangeRef.current[2] !== tfSec;
    seriesRef.current.setData(candles);
    rangeRef.current = [candles[0].time, candles[candles.length - 1].time, tfSec];
    if (first) chartRef.current.timeScale().fitContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candles]);

  useEffect(() => {
    if (!seriesRef.current || !candles.length) return;
    const [lo, hi] = [candles[0].time, candles[candles.length - 1].time];
    const map = {};
    activity.forEach((a) => { const t = bucket(a, tfSec); if (t >= lo && t <= hi) (map[t] = map[t] || []).push(a); });
    const markers = Object.keys(map).map(Number).sort((x, y) => x - y).flatMap((t) => groupMarkers(t, map[t]));
    evRef.current = map;
    seriesRef.current.setMarkers(markers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity, candles]);

  const opens = activity.filter((a) => a.kind === "open").length;
  const wins = activity.filter((a) => a.kind === "win").length;
  const losses = activity.filter((a) => a.kind === "loss").length;

  return (
    <div className="mt-4 rounded-lg bg-fx-base border border-white/5 p-3" data-testid="bot-live-graph">
      <div className="flex items-center justify-between mb-1">
        <span className={clsx("flex items-center gap-1.5 text-xs font-semibold", running ? "text-pos" : "text-fx-s3")}>
          <span className="relative flex h-2 w-2">
            {running && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pos opacity-75" />}
            <span className={clsx("relative inline-flex rounded-full h-2 w-2", running ? "bg-pos" : "bg-fx-s3")} />
          </span>
          {asset} · Activity Analysis <span className="font-mono text-fx-s3 font-normal">{tfLabel}</span>
        </span>
        <span className="text-[10px] font-mono text-fx-s3 flex gap-2" data-testid={`bot-graph-stats-${lid}`}>
          <span className="text-brandblue">{opens} entries</span><span className="text-pos">{wins}W</span><span className="text-neg">{losses}L</span>
        </span>
      </div>
      <div className="relative" onMouseLeave={() => setTip(null)}>
        <div ref={boxRef} className="w-full" data-testid={`bot-activity-chart-${lid}`} />
        {tip && (
          <div className="pointer-events-none absolute z-10 max-w-[240px] rounded-md border border-white/10 bg-fx-s2/95 backdrop-blur px-2.5 py-1.5 shadow-xl"
            style={{ left: Math.min(tip.x + 12, (boxRef.current?.clientWidth || 300) - 250), top: Math.max(0, tip.y - 40) }} data-testid="bot-graph-tooltip">
            {tip.evs.slice(0, 4).map((e) => (
              <div key={e.id} className="text-[11px] leading-snug py-0.5">
                <div className={clsx("font-mono", e.kind === "win" ? "text-pos" : e.kind === "loss" ? "text-neg" : e.kind === "open" ? "text-brandblue" : "text-gold")}>{e.message}</div>
                <div className="text-[10px] text-fx-s3">{new Date(e.created_at).toLocaleTimeString()}</div>
              </div>
            ))}
            {tip.evs.length > 4 && <div className="text-[10px] text-fx-s3 pt-0.5">+{tip.evs.length - 4} more in this candle</div>}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-3 mt-1 text-[10px] font-mono text-fx-s3">
        <span><span className="text-pos">▲</span> Buy</span><span><span className="text-neg">▼</span> Sell</span>
        <span><span className="text-pos">●</span> Win</span><span><span className="text-neg">●</span> Loss</span>
        <span><span className="text-gold">■</span> Start</span><span><span className="text-fx-s3">■</span> Pause/Stop</span>
      </div>
    </div>
  );
}
